import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  getInflightCount,
  setRegion as setAxiosRegion,
  subscribeInflight,
} from '../api/client';
import { useAuth, type Region } from './AuthContext';

const STORAGE_KEY = 'currentRegion';
const INFLIGHT_WAIT_MS = 5_000;

type SwitchStatus =
  | { state: 'idle' }
  | { state: 'draining'; target: Region; startedAt: number }
  | { state: 'awaiting-confirmation'; target: Region; inflight: number };

interface RegionContextType {
  region: Region | null;
  availableRegions: Region[];
  isMultiRegion: boolean;
  switchStatus: SwitchStatus;
  /** Begin a region switch. Resolves when the switch has taken effect. */
  requestSwitch: (target: Region) => Promise<void>;
  /** User confirmed "force switch" in the modal — abandon inflight and switch now. */
  forceSwitch: () => void;
  /** User cancelled the switch in the modal — keep current region. */
  cancelSwitch: () => void;
}

const RegionContext = createContext<RegionContextType | undefined>(undefined);

export function RegionProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const [region, setRegionState] = useState<Region | null>(null);
  const [switchStatus, setSwitchStatus] = useState<SwitchStatus>({ state: 'idle' });
  const pendingResolve = useRef<(() => void) | null>(null);

  const availableRegions = useMemo<Region[]>(() => user?.regions ?? [], [user]);
  const isMultiRegion = availableRegions.length > 1;

  // Initialize region once the user is available. Prefer previously-chosen region
  // if it's still permitted; otherwise fall back to the user's default.
  useEffect(() => {
    if (!isAuthenticated || !user) {
      setRegionState(null);
      setAxiosRegion(null);
      return;
    }
    const stored = localStorage.getItem(STORAGE_KEY) as Region | null;
    const initial = stored && user.regions.includes(stored) ? stored : user.defaultRegion;
    setRegionState(initial);
    setAxiosRegion(initial);
  }, [isAuthenticated, user]);

  const commitSwitch = useCallback(
    (target: Region) => {
      setRegionState(target);
      setAxiosRegion(target);
      localStorage.setItem(STORAGE_KEY, target);
      setSwitchStatus({ state: 'idle' });
      // Refetch all active queries so the UI reflects the new region's data.
      queryClient.cancelQueries();
      queryClient.invalidateQueries();
      pendingResolve.current?.();
      pendingResolve.current = null;
    },
    [queryClient],
  );

  const requestSwitch = useCallback(
    async (target: Region) => {
      if (!user || !user.regions.includes(target)) {
        throw new Error(`Region ${target} not permitted for this user`);
      }
      if (target === region) return;

      // Nothing in-flight? Switch immediately.
      if (getInflightCount() === 0) {
        commitSwitch(target);
        return;
      }

      // Wait up to INFLIGHT_WAIT_MS for the queue to drain.
      return new Promise<void>((resolve) => {
        pendingResolve.current = resolve;
        setSwitchStatus({ state: 'draining', target, startedAt: Date.now() });

        const unsubscribe = subscribeInflight((count) => {
          if (count === 0 && pendingResolve.current) {
            unsubscribe();
            clearTimeout(timer);
            commitSwitch(target);
          }
        });

        const timer = setTimeout(() => {
          unsubscribe();
          if (pendingResolve.current) {
            setSwitchStatus({
              state: 'awaiting-confirmation',
              target,
              inflight: getInflightCount(),
            });
          }
        }, INFLIGHT_WAIT_MS);
      });
    },
    [user, region, commitSwitch],
  );

  const forceSwitch = useCallback(() => {
    if (switchStatus.state !== 'awaiting-confirmation') return;
    // Cancelling active React Query fetches also aborts their axios requests.
    queryClient.cancelQueries();
    commitSwitch(switchStatus.target);
  }, [switchStatus, queryClient, commitSwitch]);

  const cancelSwitch = useCallback(() => {
    setSwitchStatus({ state: 'idle' });
    pendingResolve.current?.();
    pendingResolve.current = null;
  }, []);

  return (
    <RegionContext.Provider
      value={{
        region,
        availableRegions,
        isMultiRegion,
        switchStatus,
        requestSwitch,
        forceSwitch,
        cancelSwitch,
      }}
    >
      {children}
    </RegionContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useRegion(): RegionContextType {
  const ctx = useContext(RegionContext);
  if (!ctx) throw new Error('useRegion must be used within RegionProvider');
  return ctx;
}
